import { apiExecutePost, mutateWithVariables, query, queryWithVariables } from '@/services/api/api';
import { Order } from '@/types/api/Business/Order';
import { ApiResponse } from '@/types/api/Utility/ApiResponse';
import { FenceSunService } from '@/types/constants/fence-sun';
const ReportServiceUrl = import.meta.env.VITE_REPORT_SERVICE_URL;
const ReportServiceApiKey = import.meta.env.VITE_REPORT_SERVICE_API_KEY;
/* eslint-disable @typescript-eslint/no-explicit-any*/

export interface OrderRequestData {
  product: string;
  colorList: (string | null)[];
  embossingList: (string | null)[];
  embossingColorList: (string | null)[];
  wtransferList: (string | null)[];
  varnish: string;
  technicianFirstname: string;
  technicianLastname: string;
  technician: string;
  company: string;
  customerNr: string;
  orderNr: string;
  billingNr: string;
  commissionNo: string;
  vrNumber: string;
  vgNumber: string;
  vsgNumber: string;
  mail: string;
  orderMail: string;
  coverModel: string;
  prothesisType: string;
  bodySide: string;
  kneeJoint: string;
  foot: string;
  zip: string;
  sewInByCustomer: boolean;
  cosmeticEndingAtFoot: boolean;
  heightWithoutDeduction: number;
  heightDeduction: number;
  heightWithDeduction: number;
  depth: number;
  calfCircumferenceWithoutDeduction: number;
  calfCircumferenceDeduction: number;
  calfCircumferenceWithDeduction: number;
  ankleCircumference: number;
  imageFiles: any[];
  scanFiles: any[];
  designFiles: string[];
  remarks: string;
  price: number;
  balconyType: string;
  mountingType: string;
  paneelType: string;
  orientationType: string;
  isPrivacyAccepted: boolean;
  privacy: string;
  bilateralAmputee: boolean;
  fenceType: string;
  fenceSunService?: FenceSunService[];
}

export interface OrderTemplate {
  HTMLTemplate: string;
  HTMLConfirmationTemplate: string;
}

export interface LicenseData {
  licenseId: number;
  name: string;
  description?: string;
  price: number;
  expiryTime: number;
  stripeProductId?: string;
  stripePriceId?: string;
}

export const getOrdersByCustomer = async (
  customerUserId: number,
  paymentStatusFilter?: string,
  orderStatusFilter?: string
): Promise<Order[]> => {
  const result = await queryWithVariables(
    `
    query GetOrdersByCustomer(
      $customerUserId: Long!, 
      $paymentStatusFilter: PaymentStatus, 
      $orderStatusFilter: OrderStatus
    ) {
      ordersByCustomer(
        customerId: $customerUserId, 
        paymentStatusFilter: $paymentStatusFilter, 
        orderStatusFilter: $orderStatusFilter
      ) {
        orderId
        orderNumber
        orderDate
        deliveryDate
        billingAddress
        shippingAddress
        additionalInformation
        orderStatus
        paymentStatus
        paymentTerms
        basePricePerUnit
        quantity
        totalPrice
        tax
        taxRate
        customer {
          additionalEmails
          authentikId
          email
          firstname
          lastname
          titlePostfix
          titlePrefix
          userId
          username
        }
        supplier {
          additionalEmails
          authentikId
          email
          firstname
          lastname
          titlePostfix
          titlePrefix
          userId
          username
        }
        domain {
          domainId
          logo
          price
          released
          urlSuffix
        }
        model {
          modelId
          notice
          side
          product {
            bodyPartId
            description
            name
            productId
            productTypeId
            thumbnailId
          }
        }
        depth
        height
        width
        bodySide
        kneeJoint
        coverOrderItems {
          color
          componentId
          engraving
          engravingColor
          orderItemId
          price
          additionalPricePerUnit
          quantity
          component {
            basePrice
            componentId
              name
              unit
              uuid
            }
          }
        imageFiles
        scanFiles
        }
      }
    `,
    {
      customerUserId,
      paymentStatusFilter: paymentStatusFilter ?? null,
      orderStatusFilter: orderStatusFilter ?? null,
    },
    new AbortController(),   // <- keep if you want cancellation support
    true                     // <- FORCE network-only for this query
  );


  return result.data.ordersByCustomer;
};

export const getOrdersBySupplier = async (
  supplierUserId: number,
  customerFilter?: string,
  paymentStatusFilter?: string,
  orderStatusFilter?: string
): Promise<Order[]> => {
  const result = await queryWithVariables(
    `
    query GetOrdersBySupplier(
      $supplierUserId: Long!,
      $customerFilter: String,
      $paymentStatusFilter: PaymentStatus,
      $orderStatusFilter: OrderStatus
    ) {
      ordersBySupplier(
        supplierId: $supplierUserId,
        customerFilter: $customerFilter,
        paymentStatusFilter: $paymentStatusFilter,
        orderStatusFilter: $orderStatusFilter
      ) {
        orderId
        orderNumber
        orderDate
        deliveryDate
        billingAddress
        shippingAddress
        additionalInformation
        orderStatus
        paymentStatus
        paymentTerms
        basePricePerUnit
        quantity
        totalPrice
        tax
        taxRate
        customer {
          additionalEmails
          authentikId
          email
          firstname
          lastname
          titlePostfix
          titlePrefix
          userId
          username
        }
        supplier {
          additionalEmails
          authentikId
          email
          firstname
          lastname
          titlePostfix
          titlePrefix
          userId
          username
        }
        domain {
          domainId
          logo
          price
          released
          urlSuffix
        }
        model {
          modelId
          notice
          side
          product {
            bodyPartId
            description
            name
            productId
            productTypeId
            thumbnailId
          }
        }
        depth
        height
        width
        bodySide
        kneeJoint
        coverOrderItems {
          color
          componentId
          engraving
          engravingColor
          orderItemId
          price
          additionalPricePerUnit
          quantity
          component {
            basePrice
            componentId
              name
              unit
              uuid
            }
          }
        imageFiles
        scanFiles
        }
      }
    `,
    {
      supplierUserId,
      customerFilter: customerFilter ?? null,
      paymentStatusFilter: paymentStatusFilter ?? null,
      orderStatusFilter: orderStatusFilter ?? null,
    },
    new AbortController(),   // <- keep if you want cancellation support
    true                     // <- FORCE network-only for this query
  );

  return result.data.ordersBySupplier;
};

export const updateOrderStatus = async (
  orderId: number,
  orderStatus: string
): Promise<Order> => {
  const result = await mutateWithVariables(
    `
      mutation UpdateOrderStatus($orderId: Long!, $orderStatus: OrderStatus!) {
        updateOrderStatusByOrderId(orderId: $orderId, orderStatus: $orderStatus) {
          orderId
          orderNumber
          supplierUserId
          billingAddress
          shippingAddress
          orderDate
          deliveryDate
          orderStatus
          paymentStatus
          paymentTerms
          totalPrice
          tax
          taxRate
          customerUserId
        }
      }
    `,
    {
      orderId: orderId,
      orderStatus: orderStatus,
    }
  );

  return result.data.updateOrderStatusByOrderId;
};

export const updateOrderPaymentStatus = async (orderId: number): Promise<Order> => {
  const result = await mutateWithVariables(
    `
      mutation UpdateOrderPaymentStatus($orderId: Long!) {
        updateOrderPaymentStatusByOrderId(orderId: $orderId) {
          orderId
          orderNumber
          supplierUserId
          billingAddress
          shippingAddress
          orderDate
          deliveryDate
          orderStatus
          paymentStatus
          paymentTerms
          totalPrice
          tax
          taxRate
          customerUserId
        }
      }
    `,
    { orderId }
  );

  return result.data.updateOrderPaymentStatusByOrderId;
};

export const updateOrderFiles = async (
  orderId: number,
  scanFiles: string[],
  imageFiles: string[]
): Promise<Order> => {
  const result = await mutateWithVariables(
    `
      mutation UploadOrderFilesToOrder($orderId: Long!, $scanFiles: [URL!], $imageFiles: [URL!]) {
        uploadOrderFilesToOrder(orderId: $orderId, scanFiles: $scanFiles, imageFiles: $imageFiles) {
          orderId
          orderNumber
          supplierUserId
          billingAddress
          shippingAddress
          orderDate
          deliveryDate
          orderStatus
          paymentStatus
          paymentTerms
          totalPrice
          tax
          taxRate
          customerUserId
        }
      }
    `,
    {
      orderId,
      scanFiles,
      imageFiles
    }
  );

  return result.data.updateOrderPaymentStatusByOrderId;
};

export const createPdfPartList = async (modelId: number, domainId: number): Promise<Uint8Array> => {
  const result = await mutateWithVariables(
    `
        mutation ($modelId: Long!, $domainId: Long!) {
          createPdfPartList(
            modelId: $modelId
            domainId: $domainId
          )
        }
      `,
    {
      modelId: modelId,
      domainId: domainId,
    }
  );
  return result.data.createPdfPartList;
};

export const createPdfOffer = async (orderData: OrderRequestData, modelId: number, domainId: number): Promise<Uint8Array> => {
  const result = await mutateWithVariables(
    `
        mutation ($orderData: OrderDataInput!, $modelId: Long!, $domainId: Long!) {
          createPdfOffer(
            orderData: $orderData
            modelId: $modelId
            domainId: $domainId
          )
        }
      `,
    {
      orderData: orderData,
      modelId: modelId,
      domainId: domainId,
    }
  );
  return result.data.createPdfOffer;
};

export const sendOrderAsync = async (orderData: OrderRequestData, modelId: number, domainId: number, userId: number | null): Promise<ApiResponse> => {
  const result = await mutateWithVariables(
    `
        mutation ($orderData: OrderDataInput!, $modelId: Long!, $domainId: Long!, $userId: Long) {
          sendOrder(
            orderData: $orderData
            modelId: $modelId
            domainId: $domainId
            userId: $userId
          )
        }
      `,
    {
      orderData: orderData,
      modelId: modelId,
      domainId: domainId,
      userId: userId,
    }
  );
  return result.data;
};


export const getLicenses = async (): Promise<LicenseData[]> => {
  const queryResult = await query(
    `query {
      licenses{
        licenseId
        name
        description
        expiryTime
        price
        stripePriceId
        stripeProductId
      }
    }`
  );

  const result: LicenseData[] = [];
  for (let i = 0; i < queryResult.data.licenses.length; i++) {
    const data = queryResult.data.licenses[i];
    const license: LicenseData = {
      name: data.name,
      expiryTime: data.expiryTime,
      licenseId: data.licenseId,
      price: data.price,
      description: data.description,
      stripePriceId: data.stripePriceId,
      stripeProductId: data.stripeProductId,
    };

    result.push(license);
  }

  return result;
};

export const getLicense = async (licenseId: number): Promise<LicenseData> => {
  const queryResult = await queryWithVariables(
    `query ($licenseId: Long!) {
      license(licenseid: $licenseId) {
        licenseId
        name
        description
        expiryTime
        price
        stripePriceId
        stripeProductId
      }
    }`,
    {
      licenseId,
    }
  );

  const data = queryResult.data.license;
  const result: LicenseData = {
    name: data.name,
    expiryTime: data.expiryTime,
    licenseId: data.licenseId,
    price: data.price,
    description: data.description,
    stripePriceId: data.stripePriceId,
    stripeProductId: data.stripeProductId,
  };

  return result;
};

export const createLicenseOrder = async (licenseId: number, userId: number) => {
  const result = await mutateWithVariables(
    `mutation($licenseId: Long!, $userId: Long!){
      createOrder(
        licenseId: $licenseId,
        userId: $userId
      )
    }`,
    {
      licenseId,
      userId,
    }
  );

  return result.data;
};

export const updateInvoiceStatus = async (invoiceId: number) => {
  const status = 'SUCCESS';
  const result = await mutateWithVariables(
    `mutation($invoiceId: Long!, $status: String!){
      updateOrderPaymentStatus(
        invoiceId: $invoiceId,
        status: $status
      )
    }`,
    {
      invoiceId,
      status,
    }
  );

  return result.data;
};

export const saveOrderDataToMongoDB = async (orderRequestData: OrderRequestData) => {
  try {
    const response = await apiExecutePost(`${ReportServiceUrl}/report/save-order`, orderRequestData, window.location.origin, {
      headers: {
        'x-api-key': ReportServiceApiKey,
      },
    });
    console.log('Response from server:', response);
  } catch (error) {
    console.error('Error during API call:', error);
  }
};
